/**
 * COLOUR MATHS, done here rather than asked of the browser.
 *
 * The prototype measured contrast through a hidden probe and `getComputedStyle`.
 * Its own comment records what that cost: Chromium answers `rgb(0,0,0)`, or
 * `color(srgb 0 0 0)`, or `oklch(…)` depending on the day, and assuming one
 * format gave "ratios of 1:1 measured on a perfectly readable button". Doing the
 * arithmetic ourselves removes that entire class of bug, and makes every rule
 * below testable without a browser.
 *
 * OKLCH throughout, because it is the only space in common use where moving
 * lightness leaves the hue alone — which is exactly what deriving a palette
 * from one brand colour requires.
 */

export type Oklch = { l: number; c: number; h: number };

const cube = (x: number) => x * x * x;

/** sRGB gamma → linear. The WCAG formula, unchanged since 2008. */
function toLinear(v: number): number {
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
}

function toGamma(v: number): number {
  return v <= 0.0031308 ? v * 12.92 : 1.055 * v ** (1 / 2.4) - 0.055;
}

function clamp(v: number, min = 0, max = 1): number {
  return Math.min(max, Math.max(min, v));
}

/** `#rrggbb` (or `#rgb`) → the three channels, 0–1. Anything else throws. */
export function parseHex(hex: string): [number, number, number] {
  const s = hex.trim().replace(/^#/, "");
  const full =
    s.length === 3
      ? s
          .split("")
          .map((c) => c + c)
          .join("")
      : s;
  if (!/^[0-9a-f]{6}$/i.test(full))
    throw new Error(`Couleur illisible : ${hex}`);
  return [
    parseInt(full.slice(0, 2), 16) / 255,
    parseInt(full.slice(2, 4), 16) / 255,
    parseInt(full.slice(4, 6), 16) / 255,
  ];
}

/**
 * CE QU'UN ORGANISATEUR TAPE → `#rrggbb`, ou `null` tant que ça ne dit rien.
 *
 * ⚠️ UN NUANCIER DE MARQUE N'ÉCRIT PAS CE QUE LE NAVIGATEUR ATTEND. Il donne
 * « 91BC8F », « #91bc8f », parfois « #8f9 » — trois façons de nommer la même
 * couleur, et `input type="color"` n'en accepte qu'une, en minuscules et sur six
 * chiffres. Coller son code de marque devait rester le geste le plus court.
 *
 * `null` est la réponse normale PENDANT LA FRAPPE : « #91b » n'est pas une couleur,
 * c'est un début, et l'écrire repeindrait le site sur un demi-code.
 */
export function normalizeHex(raw: string): string | null {
  const s = raw.trim().replace(/^#/, "");
  if (!/^(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(s)) return null;
  const full =
    s.length === 3
      ? s
          .split("")
          .map((c) => c + c)
          .join("")
      : s;
  return `#${full.toLowerCase()}`;
}

/**
 * CE QU'ON TAPE PEUT-IL ENCORE DEVENIR UNE COULEUR ?
 *
 * ⚠️ UN CODE INCOMPLET N'EST PAS UN CODE FAUX, et les traiter pareil serait crier au
 * loup : « #91b » est ce qu'on voit au milieu de la frappe de « #91bc8f ». Seul ce qui
 * ne peut plus aboutir — une lettre qui n'est pas un chiffre hexadécimal, un septième
 * caractère — mérite d'être signalé à l'organisateur.
 */
export function looksLikeHex(raw: string): boolean {
  return /^#?[0-9a-f]{0,6}$/i.test(raw.trim());
}

/**
 * LE CODE EST-IL COMPLET — six chiffres, celui qu'on peut appliquer SANS ATTENDRE ?
 *
 * ⚠️ « #91B » EST UNE COULEUR VALIDE, et c'est tout le piège : en route vers
 * « #91bc8f », la frappe passe par un code à trois chiffres qui vaut `#9911bb`.
 * Appliqué à la volée, il repeindrait l'événement en violet le temps de trois
 * touches, sous les yeux de l'organisateur qui tape son vert. Un code court est donc
 * pris quand on QUITTE le champ, jamais pendant la frappe.
 */
export function isFullHex(raw: string): boolean {
  return /^#?[0-9a-f]{6}$/i.test(raw.trim());
}

function toHex([r, g, b]: [number, number, number]): string {
  const byte = (v: number) =>
    Math.round(clamp(v) * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${byte(r)}${byte(g)}${byte(b)}`;
}

/**
 * ═══ LA COULEUR TELLE QUE LE SÉLECTEUR LA MONTRE : TEINTE, SATURATION, VALEUR ═══
 *
 * Le carré et le rail du sélecteur (`panel/ColorPicker.tsx`) sont un espace TSV :
 * le rail choisit la teinte, le carré promène la saturation de gauche à droite et
 * la valeur de haut en bas. C'est l'espace de la fenêtre native que le sélecteur
 * remplace, et celui de tous les outils où l'organisateur a déjà tenu ce geste.
 *
 * ⚠️ PAS OKLCH, QUI EST POURTANT L'ESPACE DE TOUT LE RESTE DE CE FICHIER. Un carré
 * OKLCH a des coins que sRGB ne sait pas peindre : un tiers du rectangle y rend la
 * même couleur écrêtée, et le curseur s'y promène sans que rien ne change. TSV tient
 * dans sRGB par construction — chaque point du carré est une couleur, et une seule.
 * OKLCH reste ce qui DÉRIVE une palette et ce qui mesure un contraste ; TSV est ce
 * qu'on manipule à la main.
 */
export type Hsv = { h: number; s: number; v: number };

/** `#rrggbb` → teinte (0–360), saturation et valeur (0–1). */
export function hexToHsv(hex: string): Hsv {
  const [r, g, b] = parseHex(hex);
  const max = Math.max(r, g, b);
  const span = max - Math.min(r, g, b);

  /* ⚠️ UN GRIS N'A PAS DE TEINTE — 0° est ce qu'on répond faute de mieux, c'est-à-dire
     du ROUGE. Le sélecteur garde donc la teinte du rail en mémoire plutôt que de la
     relire de la couleur : sans ça, ramener la saturation à zéro ferait sauter le rail
     sur le rouge, et l'organisateur perdrait la teinte qu'il cherchait. */
  let h = 0;
  if (span > 0) {
    const sector =
      max === r ? (g - b) / span : max === g ? (b - r) / span + 2 : (r - g) / span + 4;
    h = ((sector * 60) % 360 + 360) % 360;
  }
  return { h, s: max === 0 ? 0 : span / max, v: max };
}

/** Teinte, saturation, valeur → `#rrggbb`. L'inverse exact de `hexToHsv`, à l'arrondi près. */
export function hsvToHex({ h, s, v }: Hsv): string {
  const hue = ((h % 360) + 360) % 360;
  const sat = clamp(s);
  const val = clamp(v);
  const channel = (n: number) => {
    const k = (n + hue / 60) % 6;
    return val - val * sat * clamp(Math.min(k, 4 - k), 0, 1);
  };
  return toHex([channel(5), channel(3), channel(1)]);
}

export function hexToOklch(hex: string): Oklch {
  const [r, g, b] = parseHex(hex).map(toLinear) as [number, number, number];

  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);

  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;

  const c = Math.hypot(A, B);
  // A grey has no hue. Reporting 0 rather than an arbitrary angle keeps the
  // derived palette grey instead of tinting it by accident.
  const h = c < 1e-4 ? 0 : ((Math.atan2(B, A) * 180) / Math.PI + 360) % 360;
  return { l: L, c, h };
}

export function oklchToHex({ l, c, h }: Oklch): string {
  const rad = (h * Math.PI) / 180;
  const A = c * Math.cos(rad);
  const B = c * Math.sin(rad);

  const l_ = cube(l + 0.3963377774 * A + 0.2158037573 * B);
  const m_ = cube(l - 0.1055613458 * A - 0.0638541728 * B);
  const s_ = cube(l - 0.0894841775 * A - 1.291485548 * B);

  return toHex([
    toGamma(clamp(4.0767416621 * l_ - 3.3077115913 * m_ + 0.2309699292 * s_)),
    toGamma(clamp(-1.2684380046 * l_ + 2.6097574011 * m_ - 0.3413193965 * s_)),
    toGamma(clamp(-0.0041960863 * l_ - 0.7034186147 * m_ + 1.707614701 * s_)),
  ]);
}

/** WCAG relative luminance. */
export function luminance(hex: string): number {
  const [r, g, b] = parseHex(hex).map(toLinear) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio, 1 (identical) to 21 (black on white). */
export function contrast(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** A background is "dark" when white text reads better on it than black. */
export function isDark(hex: string): boolean {
  return contrast("#ffffff", hex) > contrast("#000000", hex);
}

/**
 * A colour of the given hue that is READABLE on `background`.
 *
 * Starts at the background's own lightness and walks away from it — down on a
 * light ground, up on a dark one — until the MEASURED ratio reaches the target.
 * Not a guessed lightness: the same hue needs very different lightness against
 * white and against navy, and guessing is how "accessible by design" palettes
 * end up at 2.3:1.
 *
 * Returns the best it found if the target is out of reach, because a slightly
 * short ratio beats a colour picked at random.
 */
export function readableOn(
  background: string,
  hue: number,
  chroma: number,
  target: number,
): string {
  const dark = isDark(background);
  const from = hexToOklch(background).l;
  const step = dark ? 0.02 : -0.02;

  let best = background;
  let bestRatio = 0;
  for (let i = 1; i <= 60; i++) {
    const l = clamp(from + step * i, 0.02, 0.99);
    const candidate = oklchToHex({ l, c: chroma, h: hue });
    const ratio = contrast(candidate, background);
    if (ratio >= target) return candidate;
    if (ratio > bestRatio) {
      bestRatio = ratio;
      best = candidate;
    }
    // Stop only once the walk is PINNED IN ITS OWN DIRECTION. Testing both ends
    // broke on the first step from a pure black ground: lightness was already at
    // the floor, the loop quit at once, and the text came back the same black as
    // the page. 1:1, caught by the test that walks hostile pairs.
    if (step > 0 ? l >= 0.99 : l <= 0.02) break;
  }
  return best;
}

/**
 * ⚠️ UNE COULEUR SANS CHROMA N'A PAS DE TEINTE — et lui en prêter une était le défaut.
 * `hexToOklch("#ffffff")` rend `h = 0` faute de mieux, c'est-à-dire du ROUGE ; blanc et
 * noir n'ont pourtant aucune teinte à faire valoir. En dessous de ce seuil, la teinte du
 * mélange est celle de l'autre couleur, sans interpolation.
 */
const NO_HUE = 0.002;

/** Mixes two colours in OKLab — a perceptual blend, not an sRGB smear. */
export function mix(a: string, b: string, amount: number): string {
  const A = hexToOklch(a);
  const B = hexToOklch(b);
  const t = clamp(amount);

  /**
   * ═══ LE LAVIS DE MARQUE SORTAIT ROSE, SUR TOUS LES ÉVÉNEMENTS ═══
   *
   * Mesuré en dev sur un événement VERT (`#91bc8f`) posé sur une page BLANCHE :
   * `--gk-brand-50/100/200` valaient `#fdf9f9`, `#fcf3f3`, `#f8e7e3` — trois roses. La
   * teinte partait de 0° (le blanc, faute de mieux) et n'arrivait qu'à 17° à 12 % : du
   * rouge pâle, sur toutes les pastilles douces du site — les tuiles de l'accueil, le
   * rond d'une réponse retenue, la pastille de rôle du badge, le fond d'un onglet
   * courant. C'est ce qui rendait le bandeau « Votre badge vous attend » vert sur rose.
   *
   * Celui qui A une teinte l'impose donc ; il ne reste alors qu'à interpoler la clarté et
   * le chroma, ce qui est exactement ce qu'on veut d'un lavis.
   */
  const hueA = A.c < NO_HUE ? B.h : A.h;
  const hueB = B.c < NO_HUE ? A.h : B.h;

  // Through the shortest arc: 350° and 10° should meet at 0°, not at 180°.
  let dh = hueB - hueA;
  if (dh > 180) dh -= 360;
  if (dh < -180) dh += 360;
  return oklchToHex({
    l: A.l + (B.l - A.l) * t,
    c: A.c + (B.c - A.c) * t,
    h: (hueA + dh * t + 360) % 360,
  });
}

/**
 * ═══ UN LAVIS DE MARQUE : LA TEINTE DE LA MARQUE, LA CLARTÉ DU FOND ═══
 *
 * Ce que `mix` ne fait pas, et ne doit pas faire : `mix` interpole AUSSI la teinte, par
 * le plus court arc, et à 6 ou 10 % la teinte reste presque celle du fond. Un lavis de
 * marque sortait donc de la couleur du FOND — bleu sur une page bleue quand la marque
 * est verte, crème sur une page crème quand elle est bleue, et violet sur une page
 * marine quand elle est jaune, l'arc le plus court entre les deux passant par le
 * magenta. La réponse choisie, l'étape en cours et les pastilles ne ressemblaient plus
 * à la marque dont elles portent le nom.
 *
 * Ici la teinte EST celle de la marque ; la clarté va du fond vers la marque de
 * `amount`, et le chroma est la part `amount` de celui de la marque — la saturation du
 * fond, d'une autre teinte, n'a rien à faire dans le lavis. Sur un fond blanc (sans
 * teinte), c'est exactement ce que `mix` donnait : rien ne change là où tout allait bien.
 *
 * Une marque grise (sans teinte) garde celle du fond : il n'y a pas de teinte à imposer.
 */
export function tint(base: string, brand: string, amount: number): string {
  const A = hexToOklch(base);
  const B = hexToOklch(brand);
  const t = clamp(amount);
  if (B.c < NO_HUE) return mix(base, brand, amount);
  return oklchToHex({
    l: A.l + (B.l - A.l) * t,
    c: B.c * t,
    h: B.h,
  });
}

/**
 * UNE COULEUR POSÉE EN TRANSPARENCE SUR UNE AUTRE, comme le navigateur la compose.
 *
 * En sRGB, canal par canal : c'est ce que fait `background` quand une couche `rgba()`
 * recouvre une image. Pas `mix`, qui mélange en OKLCH : le résultat ne serait pas celui
 * qu'on voit à l'écran, et un contraste mesuré dessus mentirait.
 */
export function over(front: string, alpha: number, back: string): string {
  const a = clamp(alpha);
  const f = parseHex(front);
  const b = parseHex(back);
  return toHex([
    f[0] * a + b[0] * (1 - a),
    f[1] * a + b[1] * (1 - a),
    f[2] * a + b[2] * (1 - a),
  ]);
}
